import { useTranslation } from 'react-i18next';
import {
  Rocket,
  Palette,
  Brush,
  Scissors,
  Code2,
  Wrench,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { PageTransition, StaggerItem } from '../app/page-transition.js';
import { TableOfContents } from '../../components/ui/table-of-contents/table-of-contents.js';

// The public docs site (docs.agentbrush.dev) is NOT live yet. Until it is, this
// page shows a PREVIEW of the planned docs as non-clickable cards — it must NOT
// link out to a non-existent destination (shipping dead links is the bug this
// replaced). The regression guard in docs-page.test.tsx asserts there are no
// links to docs.agentbrush.dev. When the docs site goes live, re-introduce the
// links (and the test will then enforce the cards link out again).

type DocCategory = {
  slug: string;
  titleKey: string;
  metaKey: string;
  gradient: string;
  iconTone: 'dark' | 'light';
  icon: LucideIcon;
};

// Gradients lifted verbatim from _bmad-output/planning-artifacts/
// ux-design-directions.html. iconTone picks a white vs. canvas-black icon
// so it stays legible against the gradient luminance.
const DOC_CATEGORIES: ReadonlyArray<DocCategory> = [
  {
    slug: 'getting-started',
    titleKey: 'docs.category.getting-started.title',
    metaKey: 'docs.category.getting-started.meta',
    gradient: 'linear-gradient(135deg, #c8f050, #a8e060, #DDFC74)',
    iconTone: 'dark',
    icon: Rocket,
  },
  {
    slug: 'presets',
    titleKey: 'docs.category.presets.title',
    metaKey: 'docs.category.presets.meta',
    gradient: 'linear-gradient(135deg, #DDFC74, #2de1fc)',
    iconTone: 'dark',
    icon: Palette,
  },
  {
    slug: 'brand-identity',
    titleKey: 'docs.category.brand-identity.title',
    metaKey: 'docs.category.brand-identity.meta',
    gradient: 'linear-gradient(135deg, #c070a0, #db93b0, #f0b0d0)',
    iconTone: 'dark',
    icon: Brush,
  },
  {
    slug: 'mask-editor',
    titleKey: 'docs.category.mask-editor.title',
    metaKey: 'docs.category.mask-editor.meta',
    gradient: 'linear-gradient(135deg, #8040c0, #a060e0, #c090f0)',
    iconTone: 'light',
    icon: Scissors,
  },
  {
    slug: 'api-reference',
    titleKey: 'docs.category.api-reference.title',
    metaKey: 'docs.category.api-reference.meta',
    gradient: 'linear-gradient(135deg, #1a4fff, #256eff, #5090ff)',
    iconTone: 'light',
    icon: Code2,
  },
  {
    slug: 'mcp-tools',
    titleKey: 'docs.category.mcp-tools.title',
    metaKey: 'docs.category.mcp-tools.meta',
    gradient: 'linear-gradient(135deg, #1abfdf, #2de1fc, #80f0ff)',
    iconTone: 'dark',
    icon: Wrench,
  },
];

export function DocsPage() {
  const { t } = useTranslation('content');

  return (
    <PageTransition>
      <div className="flex w-full justify-between gap-12 px-6 pb-32 pt-16 standard:px-10">
        <div className="min-w-0 flex-1 wide:max-w-[960px]">
          <StaggerItem>
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Docs
            </p>
            <h1 className="mt-4 max-w-[20ch] font-sans text-[clamp(36px,6vw,56px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
              {t('docs.heading')}
            </h1>
            <p className="mt-4 max-w-[60ch] text-[16px] leading-[1.6] text-secondary">
              {t('docs.intro')}
            </p>
            <div
              data-testid="docs-coming-soon"
              className="mb-14 mt-6 inline-flex items-center gap-2 rounded-lg border border-accent-cyan/30 bg-accent-cyan/[0.06] px-4 py-2.5 text-[14px] text-secondary"
            >
              <Clock className="h-4 w-4 shrink-0 text-accent-cyan" aria-hidden="true" />
              {t(
                'docs.comingSoon',
                'Full documentation is coming soon — here is a preview of what it will cover.',
              )}
            </div>
          </StaggerItem>

          <StaggerItem>
            <div
              id="categories"
              className="scroll-mt-24 grid grid-cols-1 gap-5 standard:grid-cols-2 wide:grid-cols-3"
            >
              {DOC_CATEGORIES.map((category) => (
                <DocCard
                  key={category.slug}
                  title={t(category.titleKey)}
                  meta={t(category.metaKey)}
                  gradient={category.gradient}
                  iconTone={category.iconTone}
                  Icon={category.icon}
                />
              ))}
            </div>
          </StaggerItem>
        </div>
        <TableOfContents items={[{ id: 'categories', label: 'Categories' }]} />
      </div>
    </PageTransition>
  );
}

// Non-clickable preview card. Intentionally NOT an <a> — see the file header:
// the docs destination isn't live yet, so these must not be dead links.
function DocCard({
  title,
  meta,
  gradient,
  iconTone,
  Icon,
}: {
  title: string;
  meta: string;
  gradient: string;
  iconTone: 'dark' | 'light';
  Icon: LucideIcon;
}) {
  const iconColorClass = iconTone === 'light' ? 'text-white/85' : 'text-canvas/80';

  return (
    <div className="block" data-testid="doc-card">
      <div
        className="relative aspect-square w-full overflow-hidden rounded-lg"
        style={{ background: gradient }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className={`h-12 w-12 ${iconColorClass}`} strokeWidth={1.5} aria-hidden="true" />
        </div>
      </div>
      <div className="mt-3 px-0.5">
        <h3 className="m-0 text-[14px] font-semibold leading-[1.4] text-primary">{title}</h3>
        <p className="m-0 mt-1 text-[12px] font-medium text-muted">{meta}</p>
      </div>
    </div>
  );
}
